/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Box } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  selectHiddenTerminalsList,
  selectOverlaysCurrentContext,
  selectSelectedDatapathUuidInTransportMonitoring,
  selectSelectedTerminalTabIndex,
  selectSelectedTerminalTabIndexInTransportMonitoring,
  selectTailDetailsPanelTerminal,
  selectTerminalType,
} from '../../../../store';
import { switchOverlaysContext } from '../../../../store/actions';
import { OverlaysContextsEnum } from '../../../../store/maps/reducer';
import { getTailDetailsPanelTerminal } from '../../../../store/terminals/actions';
import {
  cancelHaloNetSpaceVehiclePolls,
  requestHaloNetSpaceVehicleOrbits,
  requestHaloNetSpaceVehicles,
} from '../../../../store/haloNetSpaceVehicles/actions';
import {
  clearSelectedDatapathUuidInPlatformList,
  clearSelectedTerminalTabIndex,
  openDetailsPanel,
  setSelectedDatapathUuidInPlatformList,
  setSelectedTerminalTabIndex,
} from '../../../../store/ui/actions';
import ErrorBoundary from '../../../common/layout/ErrorBoundary';
import { MapActionsContext } from '../../../common/map/BaseMapContainer';
import { FULL_SPACE_VEHICLE_VIEW_MAP_HEIGHT } from './FullSpaceVehicleViewMapContainer';
import { CHART_TAB_VALUES, ChartTabs } from './charts/ChartTabs';
import ChartsContainer, { CHARTS_CONTAINER_WIDTH } from './charts/ChartsContainer';
import PerformanceChartsContainer from './charts/performance/PerformanceChartsContainer';
import TransportPlansChartChartContainer from './charts/transportPlans/TransportPlansChartContainer';
import appTheme from '../../../common/theme/appTheme.json';
import TailDetailsPanel from '../../../fleet/TailDetailsPanel/TailDetailsPanel';
import { DetailsPanelEnum } from '../../../../shared/entities/detailsPanel';
import { getTerminalHistoryOnce, setAnnotations } from '../../../../store/terminalHistory/actions';
import { isNil } from 'lodash';
import { PortalsEnum } from '../../../../shared/entities/portal';
import { InstallationType, TerminalTypeLabel } from '../../../../shared/entities/terminal';
import { getSelectedTabBasedOnTerminalTypeLabel } from '../../../../utils/TerminalUtilities';

export const SPACE_VEHICLE_DETAILS_PANEL_WIDTH = 360;

const FullSpaceVehicleViewContainer = () => {
  // Local states
  const [activeTab, setActiveTab] = useState(CHART_TAB_VALUES.PERFORMANCE);

  // Selectors
  const hiddenSpaceVehicles = useSelector(selectHiddenTerminalsList, shallowEqual);
  const overlaysCurrentContext = useSelector(selectOverlaysCurrentContext);
  const tailDetailsPanelTerminal = useSelector(selectTailDetailsPanelTerminal, shallowEqual);
  const selectedTerminalTabIndex = useSelector(selectSelectedTerminalTabIndex);
  const selectedTerminalType = useSelector(selectTerminalType);
  const transportMonitoringDatapathUuid = useSelector(selectSelectedDatapathUuidInTransportMonitoring);
  const transportMonitoringTabIndex = useSelector(selectSelectedTerminalTabIndexInTransportMonitoring);

  //3rd party Hooks
  const dispatch = useDispatch();
  const { vehicleId } = useParams();

  // Context
  const mapActionsContext = useContext(MapActionsContext);
  const {
    setMapHidden,
    setMapDimensions,
    setMapPosition,
    clearMapDimensions,
    clearMapPosition,
    clearCurrentOverlaysContextExceptTailTagOptions,
  } = mapActionsContext.mapActions;

  // The selectors read the Transport monitoring context until the first effect switches the context
  const isFullSpaceVehicleViewContext = overlaysCurrentContext === OverlaysContextsEnum.FULL_SPACE_VEHICLE_VIEW;
  const terminalDetailsLoaded = tailDetailsPanelTerminal?.datapathUuid === vehicleId;
  const isHaloNetTerminal = tailDetailsPanelTerminal?.installationType === InstallationType.HALONET_TERMINAL;
  const defaultTabIndex = getSelectedTabBasedOnTerminalTypeLabel(tailDetailsPanelTerminal);
  const isRteTabSelected = isHaloNetTerminal && selectedTerminalType === TerminalTypeLabel.RTE;
  const canShowCharts =
    isFullSpaceVehicleViewContext && terminalDetailsLoaded && (!isHaloNetTerminal || !isNil(selectedTerminalTabIndex));

  const fetchChartData = useCallback(() => {
    dispatch(getTerminalHistoryOnce(vehicleId, PortalsEnum.ISAN_PORTAL));
  }, [dispatch, vehicleId]);

  // Ensure the map is shown and the context is updated
  useEffect(() => {
    dispatch(switchOverlaysContext(OverlaysContextsEnum.FULL_SPACE_VEHICLE_VIEW));
  }, [dispatch]);

  // Poll the HaloNet space vehicle list and the propagated orbits.
  // This view is routed to directly, so it cannot rely on the Transport Monitoring view for the data.
  useEffect(() => {
    dispatch(requestHaloNetSpaceVehicles());
    dispatch(requestHaloNetSpaceVehicleOrbits());

    return () => {
      dispatch(cancelHaloNetSpaceVehiclePolls());
    };
  }, [dispatch]);

  // Show map and hide on unmount
  useEffect(() => {
    dispatch(setMapHidden(false));

    return () => {
      dispatch(setMapHidden(true));
    };
  }, [dispatch, setMapHidden]);

  useEffect(() => {
    dispatch(setMapPosition(0, SPACE_VEHICLE_DETAILS_PANEL_WIDTH));
    dispatch(setMapDimensions(CHARTS_CONTAINER_WIDTH, FULL_SPACE_VEHICLE_VIEW_MAP_HEIGHT));
    return () => {
      dispatch(clearMapDimensions());
      dispatch(clearMapPosition());
    };
  }, [dispatch, setMapDimensions, setMapPosition, clearMapDimensions, clearMapPosition]);

  // set the correct vehicle for the FV2 (full vehicle view)
  useEffect(() => {
    dispatch(setSelectedDatapathUuidInPlatformList(vehicleId));
    dispatch(getTailDetailsPanelTerminal(vehicleId, PortalsEnum.ISAN_PORTAL));

    // clear the current context when switching between vehicles
    // and unmounting
    return () => {
      dispatch(clearCurrentOverlaysContextExceptTailTagOptions());
      dispatch(setAnnotations([]));
      // Reset the tab index so that the next FV2 open doesn't open the tab opened in a previous FV2 session.
      dispatch(clearSelectedTerminalTabIndex());
    };
  }, [dispatch, vehicleId, clearCurrentOverlaysContextExceptTailTagOptions]);

  // On initial FV2 load for a HaloNet vehicle, open the VI/RTE tab that was selected on the Transport monitoring
  // view, as the tab is initially set to undefined.
  useEffect(() => {
    if (
      isFullSpaceVehicleViewContext &&
      isHaloNetTerminal &&
      terminalDetailsLoaded &&
      isNil(selectedTerminalTabIndex)
    ) {
      const tabIndex = transportMonitoringDatapathUuid === vehicleId ? transportMonitoringTabIndex : undefined;
      dispatch(setSelectedTerminalTabIndex(tabIndex ?? defaultTabIndex));
    }
  }, [
    dispatch,
    isFullSpaceVehicleViewContext,
    isHaloNetTerminal,
    terminalDetailsLoaded,
    selectedTerminalTabIndex,
    transportMonitoringDatapathUuid,
    transportMonitoringTabIndex,
    vehicleId,
    defaultTabIndex,
  ]);

  // fetch fresh terminal history when the VI tab opens, as the FTV does when the dual antenna tab switches
  useEffect(() => {
    if (canShowCharts && !isRteTabSelected) {
      fetchChartData();
    }
  }, [canShowCharts, isRteTabSelected, fetchChartData]);

  // When the FV2 unmounts, if the selected terminal is not hidden on the Transport monitoring view, open the
  // Details Panel so it is visible on the Transport monitoring when the user navigates there.
  //
  // If the FV2 terminal is hidden on the Transport monitoring view, clear the selected terminal id so the Panel
  // is not visible and the hidden terminal is not selected on the Transport monitoring view
  useEffect(() => {
    return () => {
      if (hiddenSpaceVehicles.some(hiddenVehicle => hiddenVehicle === vehicleId)) {
        dispatch(clearSelectedDatapathUuidInPlatformList());
      } else {
        dispatch(openDetailsPanel());
      }
    };
  }, [hiddenSpaceVehicles, dispatch, vehicleId]);

  return (
    <Box bgcolor={appTheme.modalBackgroundColor}>
      <ErrorBoundary message="Unable to render space vehicle details panel">
        <ErrorBoundary message="Unable to render the Space Vehicle Details Panel">
          <TailDetailsPanel detailsPanelVariant={DetailsPanelEnum.SPACE_VEHICLE} isFullTailViewMode={true} />
        </ErrorBoundary>
      </ErrorBoundary>
      <ErrorBoundary message="Unable to render space vehicle charts">
        <ChartsContainer>
          <ChartTabs activeTab={activeTab} setActiveTab={setActiveTab} />
          {activeTab === CHART_TAB_VALUES.PERFORMANCE ? (
            <PerformanceChartsContainer
              canShowCharts={canShowCharts}
              isRteTabSelected={isRteTabSelected}
              onRetry={fetchChartData}
            />
          ) : (
            <TransportPlansChartChartContainer />
          )}
        </ChartsContainer>
      </ErrorBoundary>
    </Box>
  );
};

export default FullSpaceVehicleViewContainer;
